import React, { useEffect } from 'react'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import CommentRoundedIcon from '@mui/icons-material/CommentRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import { makeStyles } from '@mui/styles'
import { Portfolio, Position } from '../../redux/reducers/portfolio'
import { Typography } from '@mui/material'
import { newThemeObject } from '../../newTheme'
import { useTranslation } from 'react-i18next'
import { Security } from '../../redux/reducers/securities'
import { TransparentButton } from '../common/TransparentButton'
import { fixAdress } from '../../utils/webpage'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { formatNumber } from '../../utils/numbersFormatter'
import classNames from 'classnames'
import { InstrumentIcon } from '../common/InstrumentIcon'
import { useClientApi } from '../../hooks/useClientApi'
import { ClientPortfolioType } from '../../redux/reducers/clients'
import { format } from 'date-fns'
import { tokens } from '../../styles/tokens'

const useStyles = makeStyles(theme => ({
  detailsRow: {
    '& .MuiTableCell-root': {
      borderBottom: `2px solid ${tokens.color.neutral[300]}`,
      paddingTop: 0,
      paddingBottom: theme.spacing(2),
      background: 'transparent',
    },
  },
  detailsContent: {
    padding: theme.spacing(2.5, 0),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    margin: '0 0 12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    color: tokens.color.neutral[500],
  },
  sectionIcon: {
    fontSize: '1rem',
    color: tokens.color.primary[600],
  },
  marketInformationTable: {
    display: 'grid',
    gridTemplateColumns: `1fr 1fr `,
    gridTemplateRows: '1fr 1px 1fr 1px 1fr',
    columnGap: theme.spacing(6),
  },
  marketInformationTableLine: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    gridColumn: 'span 2',
    height: '1px',
    background: tokens.color.neutral[200],
  },
  MITitle: {
    color: newThemeObject.palette.main.sectext,
  },
  MIValue: {
    fontWeight: 600,
    color: tokens.color.neutral[900],
  },
  wrapper: {
    display: 'grid',
    gridTemplateColumns: '3fr 4fr 2fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  },
  comment: {
    color: newThemeObject.palette.main.sectext,
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
    maxHeight: '3rem',
    overflowY: 'auto',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  button: {
    backgroundColor: tokens.color.primary[50],
    color: tokens.color.primary[700],
    height: '2rem',
    borderRadius: tokens.radius.full,
    padding: '0rem 1rem',
    transition: `background-color ${tokens.transition.fast}`,
  },
  buysSection: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${tokens.color.neutral[200]}`,
  },
  noBuys: {
    color: newThemeObject.palette.main.sectext,
    fontSize: '0.8125rem',
    margin: 0,
  },

  table: {
    minWidth: 1000,
  },
  tableHead: {
    ...newThemeObject.table.tableHead, 
  },
  tableHead2: {
    '& .MuiTableCell-root': {
      background: tokens.color.neutral[100],
      margin: '10px 0 0 10px',
      height: '2rem',
      padding: '0',
      borderBottom: 'none',
    },
    '& .MuiTableCell-root:first-child': {
      borderRadius: `${tokens.radius.sm} 0 0 ${tokens.radius.sm}`,
    },
    '& .MuiTableCell-root:last-child': {
      borderRadius: `0 ${tokens.radius.sm} ${tokens.radius.sm} 0`,
    },
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  clickableRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
      paddingTop: '16px',
    },
  },
  firstClickableRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      paddingTop: '16px',
    },
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
    fontVariantNumeric: 'tabular-nums',
  },
  // Karty nakupu pro mobil - nahrazuji sirokou tabulku, aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobileCard: {
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${tokens.color.neutral[200]}`,
  },
  mobileCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mobileCardHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
  },
  mobileCardName: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.color.neutral[900],
    overflowWrap: 'break-word',
  },
  mobileCardIsin: {
    margin: 0,
    fontSize: '0.7rem',
    color: tokens.color.neutral[500],
  },
  mobileCardAmount: {
    margin: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.color.neutral[900],
    whiteSpace: 'nowrap',
  },
  mobileCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: theme.spacing(0.75, 1.5),
    marginTop: theme.spacing(1),
  },
  mobileCardField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  mobileCardLabel: {
    margin: 0,
    fontSize: '0.65rem',
    color: tokens.color.neutral[500],
  },
  mobileCardValue: {
    margin: '2px 0 0',
    fontSize: '0.7rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'break-word',
  },

}))

interface DataTableRowDetailsProps {
  position: Position
  portfolio: ClientPortfolioType | null
  getValidSecurity: (securityId: number) => Security | null
  mobile?: boolean
}

const DataTableRowDetails: React.FC<DataTableRowDetailsProps> = ({ position, getValidSecurity, portfolio, mobile }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  // const buys = useSelector(state => state.detailPortfolioState.portfolio.buys)
  const { getPositionBuys } = useClientApi()

  useEffect(()=>{
    console.log('position', position);
    if (position && position.positionId) {
      getPositionBuys(position.positionId)
    }
  }, [position])

  // useEffect(()=>{
  //   console.log('buys', buys)
  // }, [buys])

  const handleDownloadKeyInfo = () => {
    const security = getValidSecurity(position.instrument.instrumentId)
    if (!security) return

    const linkSource = `${security.fileKeyMessageHeader},${security.fileKeyMessageContent}`
    const downloadElement = document.createElement('a')
    const fileName = security.fileKeyMessageName
    downloadElement.href = linkSource
    downloadElement.download = fileName || 'empty_name.pdf'
    downloadElement.click()
  }

  const handleDownloadProspect = () => {
    const security = getValidSecurity(position.instrument.instrumentId)
    if (!security) return

    const linkSource = `${security.fileProspectHeader},${security.fileProspectContent}`
    const downloadElement = document.createElement('a')
    const fileName = security.fileProspectName
    downloadElement.href = linkSource
    downloadElement.download = fileName || 'empty_name.pdf'
    downloadElement.click()
  }

  const security = getValidSecurity(position.instrument.instrumentId)
  const hasBuys =
    !!portfolio && !!portfolio.buys && portfolio.buys.length > 0 &&
    portfolio.buys.filter(i => i.positionId === position.positionId).length > 0

  const content = (
        <div className={classes.detailsContent}>
          <div className={classes.wrapper}>
            <span>
              <p className={classes.sectionTitle}>
                <InfoRoundedIcon className={classes.sectionIcon} />
                {t('MARKET_INFORMATION').toUpperCase()}
              </p>
              <div className={classes.marketInformationTable}>
                <div className={classes.marketInformationTableLine}>
                  <p className={classes.MITitle}>{t('HORIZONTE')}</p>
                  <p className={classes.MIValue}>{security?.horizonteValue}</p>
                </div>
                <div className={classes.marketInformationTableLine}>
                  <p className={classes.MITitle}>{t('EXPERIENCE')}</p>
                  <p className={classes.MIValue}>{t(security?.skillAndExperience || '')}</p>
                </div>
                <div className={classes.divider} />
                <div className={classes.marketInformationTableLine}>
                  <p className={classes.MITitle}>{t('RISK')}</p>
                  <p className={classes.MIValue}>{security?.riskValue}</p>
                </div>
                <div className={classes.marketInformationTableLine}>
                  <p className={classes.MITitle}>{t('PREFERENCE')}</p>
                  <p className={classes.MIValue}>{t(security?.preference || '')}</p>
                </div>
                <div className={classes.divider} />
                <div className={classes.marketInformationTableLine}>
                  <p className={classes.MITitle}>{t('CATEGORY')}</p>
                  <p className={classes.MIValue}>{t(security?.category || '')}</p>
                </div>

                <div className={classes.marketInformationTableLine}>
                  <p className={classes.MITitle}>{t('RISK3')}</p>
                  <p className={classes.MIValue}>{t(security?.lossAbility || '')}</p>
                </div>
              </div>
            </span>
            <span>
              <p className={classes.sectionTitle}>
                <CommentRoundedIcon className={classes.sectionIcon} />
                {t('SECURITY_COMMENT').toUpperCase()}
              </p>
              <p className={classes.comment}>{security?.comment}</p>
            </span>
            <span>
              <p className={classes.sectionTitle}>
                <DescriptionRoundedIcon className={classes.sectionIcon} />
                {t('ADDITIONAL_INFORMATION').toUpperCase()}
              </p>
              <Typography className={classes.comment}>{security?.additionalInformation}</Typography>
            </span>
          </div>
          <div className={classes.buttons}>
            <TransparentButton
              onClick={handleDownloadProspect}
              disabled={!security?.fileProspectHeader && !security?.fileProspectContent}
              startIcon={<DownloadRoundedIcon />}
              className={classes.button}
            >
              {t('DOWNLOAD_PROSPECT')}
            </TransparentButton>

            <TransparentButton
              onClick={handleDownloadKeyInfo}
              disabled={!security?.fileKeyMessageHeader && !security?.fileKeyMessageContent}
              startIcon={<DownloadRoundedIcon />}
              className={classes.button}
            >
              {t('DOWNLOAD_KEY_MESSAGE')}
            </TransparentButton>
            <a href={fixAdress(security?.webpage?.toString())} target="blank">
              <TransparentButton disabled={!security?.webpage} startIcon={<LinkRoundedIcon />} className={classes.button}>
                {t('URL_COMPANY')}
              </TransparentButton>
            </a>
          </div>
          <div className={classes.buysSection}>
            <p className={classes.sectionTitle}>
              <ReceiptLongRoundedIcon className={classes.sectionIcon} />
              {t('TRADES').toUpperCase()}
            </p>
            {hasBuys && portfolio && mobile ? (
              <div className={classes.mobileList}>
                {portfolio.buys
                  .filter(i => i.positionId === position.positionId)[0]
                  .buys.map((position2: Position, index: number) => (
                    <div className={classes.mobileCard} key={`${position2.positionId}-${index}`}>
                      <div className={classes.mobileCardHeader}>
                        <InstrumentIcon securityType={position2.instrument.assetClass} />
                        <div className={classes.mobileCardHeaderText}>
                          <p className={classes.mobileCardName}>{position2.instrument.name}</p>
                          <p className={classes.mobileCardIsin}>{position2.instrument.isin}</p>
                        </div>
                        <p className={classes.mobileCardAmount}>
                          {formatNumber(position2.portfolioCurrencyAmount)} {portfolio.currency}
                        </p>
                      </div>
                      <div className={classes.mobileCardGrid}>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('DATE')}</p>
                          <p className={classes.mobileCardValue}>{format(new Date(position2.dateFrom), 'dd/MM/yyyy')}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('DEAL_TYPE')}</p>
                          <p className={classes.mobileCardValue}>{position2.dealType === 'SELL' ? t('DEAL_SELL') : t('DEAL_BUY')}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('ORDER_QUANTITY')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(position2.quantity, { decimalPlaces: 0 })}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('PRICE')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(position2.averagePurchasePrice)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('LAST_PRICE')}</p>
                          <p className={classes.mobileCardValue}>{formatNumber(position2.lastPrice)}</p>
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('P_L')}</p>
                          {position2.profitLoss != null ? (
                            <p
                              className={classes.mobileCardValue}
                              style={
                                position2.profitLossPercentage > 0
                                  ? { color: tokens.color.success[700] }
                                  : { color: tokens.color.primary[500] }
                              }
                            >
                              {formatNumber(position2.profitLoss) + ' (' + formatNumber(position2.profitLossPercentage) + '%)'}
                            </p>
                          ) : (
                            <p className={classes.mobileCardValue}>—</p>
                          )}
                        </div>
                        <div className={classes.mobileCardField}>
                          <p className={classes.mobileCardLabel}>{t('INVESMENT_AMOUNT')}</p>
                          <p className={classes.mobileCardValue}>
                            {formatNumber(position2.amount)} {position2.instrument.currency}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : hasBuys && portfolio ? (
              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead2}>
                  <TableRow>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])} />
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('DATE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('DEAL_TYPE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ISIN')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('NAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('TYPE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ORDER_QUANTITY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('PRICE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('LAST_PRICE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('P_L')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('INVESMENT_AMOUNT')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>
                        {t('INVESMENT_AMOUNT_IN')} {portfolio.currency}
                      </p>
                    </TableCell>                    
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolio && portfolio.buys && portfolio.buys.filter((i)=>i.positionId === position.positionId)[0].buys.map((position2: Position, index: number) => (
                    <React.Fragment key={`${position2.positionId}-${index}`}>
                      <TableRow
                        className={index === 0 ? classes.firstClickableRow : classes.clickableRow}
                      >
                        <TableCell className={classNames(classes.tableContent, classes.tableContentFirst)}>
                          <InstrumentIcon securityType={position2.instrument.assetClass} />
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{format(new Date(position2.dateFrom), 'dd/MM/yyyy')}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.dealType === 'SELL' ? t('DEAL_SELL') : t('DEAL_BUY')}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.isin}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.name}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.instrumentType}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.quantity, { decimalPlaces: 0 })}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.averagePurchasePrice)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.lastPrice)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          {position2.profitLoss != null ? (
                            <p
                              className={classes.text}
                              style={
                                position2.profitLossPercentage > 0
                                  ? { color: tokens.color.success[700], fontWeight: 700 }
                                  : { color: tokens.color.primary[500], fontWeight: 700 }
                              }
                            >
                              {formatNumber(position2.profitLoss) + ' ('+formatNumber(position2.profitLossPercentage)+'%)'}
                            </p>
                          ) : (
                            <p className={classes.text}>—</p>
                          )}
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.amount)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.currency}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.portfolioCurrencyAmount)}</p>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className={classes.noBuys}>{t('NO_TRADES_YET')}</p>
            )}
          </div>
        </div>
  )

  if (mobile) {
    return content
  }

  return (
    <TableRow className={classes.detailsRow}>
      <TableCell colSpan={14}>{content}</TableCell>
    </TableRow>
  )
}

export default DataTableRowDetails
